import {
  type DSLValueLegacy,
  type DerivedEventLegacy,
  DerivedEventTypeLegacy,
} from '@verantos-dev/types-cohorts';

export const transformDerivedEventToDsl = ({
  id,
  name,
  type,
  anyOrder,
  temporalConstraints,
  eventFilters,
  uniquenessFilters,
}: DerivedEventLegacy): DSLValueLegacy => {
  // Determine the type string for the query
  let typeString = '';
  switch (type) {
    case DerivedEventTypeLegacy.INSTANCE: {
      typeString = 'INSTANCE_EVENT';
      break;
    }
    case DerivedEventTypeLegacy.MERGED: {
      typeString = 'MERGED_EVENT';
      break;
    }
    case DerivedEventTypeLegacy.SEQUENCE: {
      typeString = 'SEQUENCE_EVENT';
      break;
    }
  }

  const simpleEventName = `${typeString}::"${name}"`;

  // Build the query
  const baseQuery = `${simpleEventName} = ${typeString} `;

  const isSequenceEvent = type === DerivedEventTypeLegacy.SEQUENCE;
  const isInstanceEvent = type === DerivedEventTypeLegacy.INSTANCE;

  const eventQuery = eventFilters
    .map(({ eventName, eventDateAttribute, isSelected, position, index }) => {
      const hasEventName = eventName.value !== '';
      const hasEventDateAttribute = eventDateAttribute.value !== '';

      // For derived events we need to transform the events that are referenced to have a prefix of 'QUALIFIED_'
      const dslEventName = [
        'MERGED_EVENT',
        'SEQUENCE_EVENT',
        'INSTANCE_EVENT',
        'CONDITION_OCCURRENCE',
        'DRUG_EXPOSURE',
        'MEASUREMENT',
        'OBSERVATION',
        'PROCEDURE_OCCURRENCE',
        'VISIT_OCCURRENCE',
      ].includes(eventName.value?.split('::')[0] || '')
        ? `QUALIFIED_${eventName.value}`
        : eventName.value;

      // TODO: handle instance events
      // isInstanceEvent
      let qualifiedEventQuery = dslEventName;
      if (qualifiedEventQuery !== '') {
        qualifiedEventQuery = `${qualifiedEventQuery}.${eventDateAttribute.value}`;
      }

      const isEventDateQuery =
        hasEventDateAttribute && isSelected?.value === 'true' ? ', IS_EVENT_DATE' : '';
      const additionalSequenceEventQuery = isSequenceEvent ? isEventDateQuery : '';

      let additionalInstanceEventQuery = '';
      if (isInstanceEvent) {
        if (hasEventName && hasEventDateAttribute) {
          additionalInstanceEventQuery = `, POSITION ( ${position?.value ?? ''}`;
          if (position?.value === 'INDEX') {
            additionalInstanceEventQuery += `( ${index?.value ?? ''} )`;
          }
          additionalInstanceEventQuery += ' )';
        }
      }
      return `DERIVED_EVENT ( ${qualifiedEventQuery}${additionalSequenceEventQuery} )${additionalInstanceEventQuery}`;
    })
    .join(',');

  const eventFiltersExist = eventFilters.length > 0;
  const temporalConstraintsExist = temporalConstraints.length > 0;

  let temporalConstraintsQuery = '';
  if (isSequenceEvent && temporalConstraintsExist) {
    temporalConstraintsQuery = temporalConstraints
      .map((temporalConstraint) => {
        return (
          'TC (' +
          temporalConstraint.inputFields.map((inputField) => inputField.value).join(' ') +
          ')'
        );
      })
      .join(',');

    if (eventFiltersExist) {
      temporalConstraintsQuery = ', ' + temporalConstraintsQuery;
    }
  }

  let uniquenessFiltersQuery = '';
  if (type === DerivedEventTypeLegacy.MERGED && uniquenessFilters?.length) {
    uniquenessFiltersQuery = uniquenessFilters
      ?.map(({ inputFields }) => {
        const uniquenessFilterQuery = inputFields?.map((inputField) => inputField.value).join(' ');
        return `UF ( ${uniquenessFilterQuery} )`;
      })
      .join(',');

    if (eventFiltersExist || temporalConstraintsExist) {
      uniquenessFiltersQuery = ', ' + uniquenessFiltersQuery;
    }
  }

  let anyOrderQuery = '';
  // if any order is checked build query
  if (anyOrder) {
    if (eventFiltersExist || temporalConstraintsExist) {
      // prefix with comma if other filters exist
      anyOrderQuery = ', ANY_ORDER';
    } else {
      // otherwise this is the only criteria, no comma is needed
      anyOrderQuery = 'ANY_ORDER';
    }
  }

  const closeOffQuery = ' ;\n';
  return {
    id,
    value:
      // Base of the query
      baseQuery +
      // begin criteria
      '(' +
      // eventFilterQueries
      eventQuery +
      // temporalConstraintQueries
      temporalConstraintsQuery +
      // uniquenessFilterQueries
      uniquenessFiltersQuery +
      // anyOrder criterion
      anyOrderQuery +
      ')' +
      // close off query
      closeOffQuery,
  };
};
